import { create } from 'zustand'
import api from '@/lib/api'
import { clearSession, hasSession } from '@/lib/authToken'

/**
 * Who is signed in. One identity provider: 42QLabs central (Authentik).
 *
 * Supabase authentication was removed, not disabled, so this store no longer holds a
 * Supabase `Session` or knows how to sign anyone in — the browser never sees a password
 * and never talks to an IdP directly. The sign-in flow is entirely server-side: the API
 * redirects to Authentik, handles the callback, and hands back a token; all this store
 * does is ask the API who that token belongs to.
 *
 * That "ask the API" matters: the local user row, not the token, is the authority on role
 * and on whether the account is active. A token can carry `application.admin` while the
 * local account is disabled, and the answer has to be no.
 */

interface User {
  id: string
  email: string
  full_name: string | null
  role: 'Admin' | 'User'
  is_active: boolean
  two_factor_enabled: boolean
}

interface AuthState {
  user: User | null
  isAuthenticated: boolean
  isLoading: boolean
  error: string | null

  logout: () => Promise<void>
  checkAuth: () => Promise<void>
  clearError: () => void
}

export const useAuthStore = create<AuthState>()((set) => ({
  user: null,
  isAuthenticated: false,
  isLoading: true,
  error: null,

  logout: async () => {
    // Best effort, then local teardown regardless: a logout that fails because the API
    // is down must still end the session in this browser.
    try { await api.post('/auth/logout') } catch { /* already gone, or unreachable */ }
    clearSession()
    set({ user: null, isAuthenticated: false, isLoading: false, error: null })
  },

  checkAuth: async () => {
    // No token means no round trip — on the login page that would be a guaranteed 401,
    // and the 401 interceptor would bounce the browser back to the page it is already on.
    if (!hasSession()) {
      set({ user: null, isAuthenticated: false, isLoading: false })
      return
    }
    try {
      const { data } = await api.get('/auth/me')
      set({ user: data, isAuthenticated: true, isLoading: false, error: null })
    } catch (e: any) {
      // 403 is a real answer — the identity is valid but has no account here — and it is
      // worth showing. Anything else is just "not signed in".
      const detail = e?.response?.status === 403
        ? (e.response.data?.detail || 'This account has no access to Plutus.')
        : null
      clearSession()
      set({ user: null, isAuthenticated: false, isLoading: false, error: detail })
    }
  },

  clearError: () => set({ error: null }),
}))

// Resolve the session once at load, before the router decides what to render.
useAuthStore.getState().checkAuth()
